// ConfirmContext.js
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import ConfirmAlertV2 from '@/components/v2/ConfirmAlert/ConfirmAlert';

interface ConfirmContextType {
  showConfirm: (
    text: ReactNode,
    onClickConfirm: () => void,
    confirmOptions?: ConfirmOptions,
  ) => void;
  hideConfirm: () => void;
}

export interface ConfirmOptions {
  confirmText?: string;
  cancelText?: string;
  subTitle?: ReactNode;
  extra?: ReactNode;
  extraClassName?: string;
  allowOutsideClickClose?: boolean;
  allowEscClose?: boolean;
  backgroundClassName?: string;
  buttonWrapperClassName?: string;
  /** 확인 버튼 색. 기본 gray */
  confirmColor?: 'gray' | 'orange';
  onClickCancel?: () => void;
}

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const useConfirmContext = () => {
  const context = useContext(ConfirmContext);
  if (context === undefined) {
    throw new Error('useConfirmContext must be used within a ConfirmProvider');
  }
  return context;
};

export const ConfirmProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [confirmContent, setConfirmContent] = useState<ReactNode>('');
  const [confirmAction, setConfirmAction] = useState<(() => void) | undefined>();
  const [confirmOptions, setConfirmOptions] = useState<ConfirmOptions>({});

  const showConfirm = useCallback(
    (text: ReactNode, onClickConfirm: () => void, confirmOptions?: ConfirmOptions) => {
      setConfirmContent(text);
      setIsVisible(true);
      if (onClickConfirm) {
        setConfirmAction(() => onClickConfirm);
      }
      if (confirmOptions) {
        setConfirmOptions(confirmOptions);
      }
    },
    [],
  );

  const hideConfirm = useCallback(() => {
    setIsVisible(false);
    setConfirmAction(undefined);
  }, []);

  const contextValue = useMemo(() => ({ showConfirm, hideConfirm }), [showConfirm, hideConfirm]);

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}

      <ConfirmAlertV2
        isOpen={isVisible}
        close={hideConfirm}
        title={confirmContent}
        onClickConfirm={confirmAction}
        {...confirmOptions}
      />
    </ConfirmContext.Provider>
  );
};
